const DB_NAME = 'transcriber_chunk_queue';
const DB_VERSION = 1;
const STORE_NAME = 'chunks';

let dbInstance = null;

/**
 * Initialize IndexedDB database
 * @returns {Promise<IDBDatabase>}
 */
export const initQueue = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      return resolve(dbInstance);
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      // Failed to open IndexedDB
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true,
        });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        objectStore.createIndex('meetingId', 'meetingId', { unique: false });
      }
    };
  });
};

/**
 * Add a chunk to the queue
 * @param {Blob} blob - The audio chunk blob
 * @param {Object} metadata - Metadata about the chunk
 * @param {string} metadata.mimeType - MIME type of the audio
 * @param {string} [metadata.meetingId] - Meeting ID
 * @returns {Promise<number>} - The ID of the stored chunk
 */
export const addChunk = async (blob, metadata = {}) => {
  try {
    const db = await initQueue();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const chunkData = {
      blob: blob,
      timestamp: Date.now(),
      mimeType: metadata.mimeType || 'audio/webm',
      meetingId: metadata.meetingId || null,
      sequence: metadata.sequence || null,
    };

    return new Promise((resolve, reject) => {
      const request = store.add(chunkData);
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        // Failed to add chunk to queue
        reject(request.error);
      };
    });
  } catch (error) {
    // Error adding chunk to queue
    throw error;
  }
};

/**
 * Get all chunks from the queue
 * @param {string} [meetingId] - Optional meeting ID to filter chunks
 * @returns {Promise<Array>} - Array of chunk objects with id, blob, and metadata
 */
export const getAllChunks = async (meetingId = null) => {
  try {
    const db = await initQueue();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        let chunks = request.result;
        // Filter by meetingId if provided
        if (meetingId) {
          chunks = chunks.filter((chunk) => chunk.meetingId === meetingId);
        }
        // Sort by timestamp to maintain order
        chunks.sort((a, b) => a.timestamp - b.timestamp);
        resolve(chunks);
      };
      request.onerror = () => {
        // Failed to get chunks from queue
        reject(request.error);
      };
    });
  } catch (error) {
    // Error getting chunks from queue
    throw error;
  }
};

/**
 * Remove a chunk from the queue by ID
 * @param {number} id - The ID of the chunk to remove
 * @returns {Promise<void>}
 */
export const removeChunk = async (id) => {
  try {
    const db = await initQueue();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        // Failed to remove chunk from queue
        reject(request.error);
      };
    });
  } catch (error) {
    // Error removing chunk from queue
    throw error;
  }
};

/**
 * Clear all chunks from the queue
 * @param {string} [meetingId] - Optional meeting ID to clear only specific meeting chunks
 * @returns {Promise<void>}
 */
export const clearQueue = async (meetingId = null) => {
  try {
    const db = await initQueue();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      if (meetingId) {
        // Clear only chunks for specific meeting
        const getAllRequest = store.getAll();
        getAllRequest.onsuccess = () => {
          const chunks = getAllRequest.result;
          const deletePromises = chunks
            .filter((chunk) => chunk.meetingId === meetingId)
            .map((chunk) => {
              return new Promise((res, rej) => {
                const deleteRequest = store.delete(chunk.id);
                deleteRequest.onsuccess = () => res();
                deleteRequest.onerror = () => rej(deleteRequest.error);
              });
            });
          Promise.all(deletePromises)
            .then(() => resolve())
            .catch((err) => reject(err));
        };
        getAllRequest.onerror = () => reject(getAllRequest.error);
      } else {
        // Clear all chunks
        const request = store.clear();
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => {
          // Failed to clear queue
          reject(request.error);
        };
      }
    });
  } catch (error) {
    // Error clearing queue
    throw error;
  }
};

/**
 * Get the number of chunks in the queue
 * @param {string} [meetingId] - Optional meeting ID to count only specific meeting chunks
 * @returns {Promise<number>}
 */
export const getQueueSize = async (meetingId = null) => {
  try {
    const db = await initQueue();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.count();
      request.onsuccess = () => {
        if (meetingId) {
          // Count only chunks for specific meeting
          const getAllRequest = store.getAll();
          getAllRequest.onsuccess = () => {
            const count = getAllRequest.result.filter(
              (chunk) => chunk.meetingId === meetingId
            ).length;
            resolve(count);
          };
          getAllRequest.onerror = () => reject(getAllRequest.error);
        } else {
          resolve(request.result);
        }
      };
      request.onerror = () => {
        // Failed to get queue size
        reject(request.error);
      };
    });
  } catch (error) {
    // Error getting queue size
    throw error;
  }
};


